import client from '../../common/graphql/apollo';
import {
  presentationQueryViewerWithSymbols,
  presentationQueryViewerWithoutSymbols,
} from '../../common/graphql/queries';
import { preloadSlideImages } from '../../common/actions/preloadActions';
import { getHashIndex } from '../../common/lib/urls';
import { loadWebFonts } from '../../common/lib/fonts';
import { CriticalError } from '../../common/lib/errors';
import { getTransitionById } from '../../common/reducers/transitionsReducer';
import apiActions from '../../common/actions/apiActions';

const embedActions = {
  initialize: () => (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const { isOffline } = getState().appReducer;
      const transitions = getState().transitionsReducer;
      const isPreview = Boolean(
        new URLSearchParams(window.location.search).get('draft') === '1',
      );

      // Data can come from GraphQL, but also directly from the window
      // object (if offline)
      let getPresentationData;
      if (isOffline) {
        getPresentationData = Promise.resolve(window.data);
      } else {
        getPresentationData = client
          .query({
            query: isPreview
              ? presentationQueryViewerWithSymbols
              : presentationQueryViewerWithoutSymbols,
            variables: {
              identifier: window.presentationId,
              password: window.password !== '' ? window.password : null,
            },
          })
          .then(async result => {
            return {
              presentation: {
                ...result.data.presentation,
              },
              slides: result.data.presentation.slides.edges.map(slide => ({
                ...slide.node,
                content: JSON.parse(slide.node.hashmapContent),
              })),
            };
          });
      }
      // Load Presentation data
      getPresentationData
        .then((
          { presentation, slides }, // migrateJSON content and inject transition data
        ) => ({
          ...presentation,
          slides: slides.map(slide => {
            const transition = slide.transition
              ? getTransitionById(transitions, slide.transition.id)
              : null;

            return {
              ...slide,
              transition: transition
                ? {
                    ...transition,
                    ...slide.transition.params,
                  }
                : isOffline
                ? slide.transition
                : null,
            };
          }),
          symbols: presentation.symbols || [],
        }))
        .then((
          presentationData, // Load webfonts
        ) =>
          loadWebFonts(presentationData.fonts, {}, isOffline).then(
            () => presentationData,
          ),
        )
        .then((
          presentationData, // Preload
        ) =>
          dispatch(
            preloadSlideImages(
              presentationData.slides,
              presentationData.symbols,
            ),
          ).then(() => presentationData),
        )
        .then(presentationData => {
          // Dispatch INITIALIZED
          const hashIndex = getHashIndex(
            window.location.hash,
            presentationData.slides.length,
          );

          dispatch({
            type: 'INIT',
            index: hashIndex - 1,
            ...presentationData,
          });

          dispatch(apiActions.healthCheck(presentationData.id));

          resolve();
        })
        .catch(error => {
          reject(new CriticalError(error.message));
        });
    }),

  refreshPresentation: props => dispatch => {
    dispatch({ type: 'REFRESH_PRESENTATION', props });
  },

  refreshSymbols: symbols => dispatch => {
    dispatch({ type: 'REFRESH_SYMBOLS', symbols });
  },

  deinitialize: () => dispatch => {
    dispatch({ type: 'DEINIT' });
  },

  didEnterFullScreen: () => dispatch => {
    dispatch({ type: 'ENTER_FULL_SCREEN' });
  },

  didExitFullScreen: () => dispatch => {
    dispatch({ type: 'EXIT_FULL_SCREEN' });
  },

  dispatchPreviousSlide: () => dispatch => {
    dispatch({ type: 'PREVIOUS_SLIDE' });
  },

  dispatchNextSlide: () => dispatch => {
    dispatch({ type: 'NEXT_SLIDE', transitionsEnabled: true });
  },

  dispatchGoToSlide: (index, transitionsEnabled = true) => dispatch => {
    dispatch({ type: 'GO_TO_SLIDE', index, transitionsEnabled });
  },

  dispatchTransitionStart: () => dispatch => {
    dispatch({ type: 'TRANSITION_START' });
  },

  dispatchTransitionComplete: () => dispatch => {
    dispatch({ type: 'TRANSITION_COMPLETE' });
  },

  setHintAsSeen: hint => dispatch => {
    dispatch({ type: 'HINT_SEEN', hint });
  },

  showVideoRecorder: () => dispatch => {
    dispatch({ type: 'SHOW_VIDEO_RECORDER' });
  },

  hideVideoRecorder: () => dispatch => {
    dispatch({ type: 'HIDE_VIDEO_RECORDER' });
  },

  showVideoViewer: () => dispatch => {
    dispatch({ type: 'SHOW_VIDEO_VIEWER' });
  },

  hideVideoViewer: () => dispatch => {
    dispatch({ type: 'HIDE_VIDEO_VIEWER' });
  },

  dispatchThumbnailGenerated: thumbnail => dispatch => {
    dispatch({ type: 'THUMBNAIL_GENERATED', thumbnail });
  },
};

export default embedActions;
